// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Custom
import Section from '@/components/sections/Section'
import { translateHref } from '@/assets/functions/links'
import { GlobalContext } from 'context/GlobalContextProvider'

// Config
import nextI18nextConfig from 'next-i18next.config'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontH3 } from '@/assets/theme/fonts'

export default function CtaSection() {
  const { t } = useTranslation('solutionsIotAirQualityMonitoring')
  const { screenDimensions } = useContext(GlobalContext)

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const getContent = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: stacked ? '1fr' : '1fr minmax(0, 24rem)',
        gap: stacked ? '2.5rem' : '3rem',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Box>
        <Typography
          variant="h2"
          component="h2"
          fontSize={getFontH3()}
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '1.25rem'
          }}
        >
          {t('cta.title')}
        </Typography>

        {[
          t('cta.description1'),
          t('cta.description2'),
          t('cta.description3')
        ].map(paragraph => (
          <Typography
            key={paragraph}
            component="p"
            fontSize={getFontBody()}
            sx={{
              lineHeight: 1.7,
              color: themeColors.gray.defaultText,
              marginBottom: '0.9rem',
              maxWidth: '40rem',
              '&:last-of-type': { marginBottom: 0 }
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </Box>

      {/* The ask, boxed off from the argument that leads up to it. */}
      <Box
        sx={{
          background: themeColors.kroemtech.background,
          border: `1px solid ${themeColors.gray.lighter}`,
          borderRadius: '12px',
          padding: '2rem'
        }}
      >
        <Typography
          variant="h3"
          component="h3"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            lineHeight: 1.3,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '0.9rem'
          }}
        >
          {t('cta.title2')}
        </Typography>

        <Typography
          component="p"
          sx={{
            fontSize: '0.95rem',
            lineHeight: 1.65,
            color: themeColors.gray.defaultText,
            marginBottom: '1.75rem'
          }}
        >
          {t('cta.note')}
        </Typography>

        <Link href={translateHref(currentLocale, 'contact')}>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosIcon sx={{ transform: 'scale(0.7)' }} />}
            sx={{
              background: themeColors.success.main,
              borderRadius: '4px',
              padding: '0.75rem 1.5rem',
              fontWeight: 600,
              letterSpacing: '0.03em',
              boxShadow: 'none',
              textAlign: 'left',
              '&:hover': {
                background: themeColors.success.darker,
                boxShadow: 'none'
              }
            }}
          >
            {t('cta.button')}
          </Button>
        </Link>
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="6rem"
      content={getContent()}
    />
  )
}
